import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
    APP_INITIALIZER,
    enableProdMode,
    importProvidersFrom,
    inject,
    provideAppInitializer,
    provideZonelessChangeDetection
} from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { bootstrapApplication, BrowserModule, Title } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { TranslateLoader, TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsModule } from '@ngxs/store';
import { TRACKING_CONFIG, TrackingSuiteModule } from '@siemens/tracking-suite-angular-lib';
import { LoginResponse, OidcSecurityService, provideAuth } from 'angular-auth-oidc-client';
import { tap } from 'rxjs';
import { Store } from '@ngxs/store';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { AuthService } from './app/auth/services/new-auth.service';
import { TokenInjector } from './app/auth/services/token.injector.service';
import { GrowthTalksMaterialsState } from './app/growth-talks/growth-talks-materials/states/growth-talks-materials.state';
import { EnhancedTranslatePipe } from './app/shared/pipes/enhanced-translation.pipe';
import { EthicalSelfReflectionState } from './app/shared/state-management/ethicalSelfReflection/ethicalSelfReflection.state';
import { SelfReflectionJourneyState } from './app/shared/state-management/selfReflectionJourney/selfReflectionJourney.state';
import { TranslationDiffState } from './app/shared/state-management/translation/translation-diff/translation-diff.state';
import { TranslationUpdateState } from './app/shared/state-management/translation/translation-update/translation.update.state';
import { TranslationState } from './app/shared/state-management/translation/translation.state';
import { FetchLanguages } from './app/shared/state-management/translation/translation.action';
import { UserState } from './app/shared/state-management/user/user.state';
import { GetAuthData, GetUserProfile } from './app/shared/state-management/user/user.actions';
import { SurveyState } from './app/shared/survey/state/survey.state';
import { HeaderSearchState } from './app/static/header/states/header-search.state';
import { environment } from './environments/environment';
import './ts/gsap';

export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
    if (environment.useLocalTranslations) {
        return new TranslateHttpLoader(http, './assets/i18n/', '.json');
    }

    return new TranslateHttpLoader(
        http,
        environment.production ? environment.translationsUrl : environment.translationsUrlDev,
        '.json'
    );
}

if (environment.production) {
    enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        provideZonelessChangeDetection(),
        importProvidersFrom(
            BrowserModule,
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: createTranslateLoader,
                    deps: [HttpClient]
                }
            }),
            NgxsModule.forRoot(
                [
                    EthicalSelfReflectionState,
                    GrowthTalksMaterialsState,
                    HeaderSearchState,
                    SelfReflectionJourneyState,
                    TranslationState,
                    TranslationDiffState,
                    TranslationUpdateState,
                    UserState,
                    SurveyState
                ],
                {
                    developmentMode: !environment.production,
                    compatibility: {
                        strictContentSecurityPolicy: true
                    }
                }
            ),
            NgxsReduxDevtoolsPluginModule.forRoot(),
            TrackingSuiteModule
        ),
        provideNativeDateAdapter(),
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        provideAnimations(),
        provideRouter(
            routes,
            withComponentInputBinding(),
            withInMemoryScrolling({
                anchorScrolling: 'enabled',
                scrollPositionRestoration: 'disabled'
            })
        ),
        provideAuth({ config: environment.authentication }),
        provideAppInitializer(() => {
            const authenticationService = inject(AuthService);
            const oidcSecurityService = inject(OidcSecurityService);

            authenticationService.isAuthenticating.set(true);

            // IMPORTANT: checkAuth() must run exactly once. We reuse the shared
            // auth state from AuthService (which performs the single checkAuth())
            // instead of calling checkAuth() a second time here. Two concurrent
            // checkAuth() calls race on the auth-code exchange and can yield a
            // LoginResponse without userData (missing gid claim).
            return authenticationService.getAuthInfo().pipe(
                tap(({ isAuthenticated }: LoginResponse) => {
                    authenticationService.isAuthenticating.set(false);
                    if (!isAuthenticated) {
                        // Not authenticated - trigger the authorization flow
                        // The browser will be redirected to the login page
                        console.info('Not authenticated, redirecting to login...');
                        oidcSecurityService.authorize();
                    }
                })
            );
        }),
        {
            provide: APP_INITIALIZER,
            useFactory: (store: Store, authService: AuthService) => {
                return () => {
                    // Load auth data and user profile after auth is initialized
                    return authService.getAuthInfo().pipe(
                        tap(() => {
                            store.dispatch(new GetAuthData());
                            store.dispatch(new GetUserProfile());
                            store.dispatch(new FetchLanguages());
                        })
                    ).toPromise();
                };
            },
            deps: [Store, AuthService],
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInjector,
            multi: true
        },
        {
            provide: TRACKING_CONFIG,
            useValue: {
                apiKey: environment.trackingSuite.apiKey,
                apiVersion: 'v3',
                endpointStage: environment.trackingSuite.endpointStage
            }
        },
        {
            provide: TranslatePipe,
            useClass: EnhancedTranslatePipe
        },
        Title
    ]
}).catch((err) => console.error(err));
